import React, { useCallback, useEffect, useMemo, useState } from 'react';
import CMECFCaseFilingPanel from './CMECFCaseFilingPanel';


const API_URL = process.env.REACT_APP_API_URL;

function formatCreatedAt(value) {
  if (!value) return '';
  return new Date(value).toLocaleString();
}

async function responseMessage(response, fallback) {
  const payload = await response.json().catch(() => ({}));
  return payload.detail || payload.error || fallback;
}

function latestDocumentPerChecklist(documents) {
  const latest = new Map();
  documents
    .filter(document => document.mergeable && document.status !== 'rejected')
    .forEach(document => {
      const current = latest.get(document.checklist_item);
      if (!current || new Date(document.uploaded_at) > new Date(current.uploaded_at)) {
        latest.set(document.checklist_item, document);
      }
    });
  return Array.from(latest.values()).map(document => document.id);
}

export default function FilingPackagePanel({ caseId, ready, onBuild }) {
  const [context, setContext] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [building, setBuilding] = useState(false);
  const [error, setError] = useState('');
  const [creditCounselingIds, setCreditCounselingIds] = useState([]);
  const [paymentAdviceIds, setPaymentAdviceIds] = useState([]);
  const [debnChoices, setDebnChoices] = useState({});
  const [serviceMethods, setServiceMethods] = useState({});
  const [preview, setPreview] = useState(null);
  const [previewingDocumentId, setPreviewingDocumentId] = useState(null);

  const loadContext = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/case/${caseId}/filing-packages/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(await responseMessage(response, 'Could not load filing packages.'));
      const payload = await response.json();
      setContext(payload);
      setCreditCounselingIds(current => current.length
        ? current
        : latestDocumentPerChecklist(payload.documents?.credit_counseling || []));
      setPaymentAdviceIds(current => current.length ? current : (
        payload.documents?.payment_advices || []
      ).filter(document => document.mergeable && document.status !== 'rejected').map(document => document.id));
      setDebnChoices(current => Object.keys(current).length ? current : Object.fromEntries(
        (payload.filing_debtors || []).map(debtor => [String(debtor.id), debtor.debn_choice])
      ));
    } catch (loadError) {
      setError(loadError.message);
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => { loadContext(); }, [loadContext]);

  useEffect(() => () => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
  }, [preview]);

  const counselingDocuments = context?.documents?.credit_counseling || [];
  const paymentDocuments = context?.documents?.payment_advices || [];
  const filingDebtorCount = context?.filing_debtors?.length || 0;
  const counselingComplete = creditCounselingIds.length >= filingDebtorCount;
  const debnChoicesComplete = (context?.filing_debtors || []).every(
    debtor => ['enroll', 'decline'].includes(debnChoices[String(debtor.id)])
  );
  const selectedServiceRecipients = useMemo(() => (
    (context?.service_creditors || [])
      .filter(creditor => serviceMethods[creditor.id])
      .map(creditor => ({ ...creditor, method: serviceMethods[creditor.id] }))
  ), [context, serviceMethods]);
  const incompleteServiceRecipient = selectedServiceRecipients.find(creditor => !creditor.address_complete);

  const toggleDocument = (id, setter) => {
    setter(current => current.includes(id)
      ? current.filter(value => value !== id)
      : [...current, id]);
  };

  const createPackage = async () => {
    if (!context?.validation?.ready || !counselingComplete || !debnChoicesComplete || incompleteServiceRecipient) return;
    setBuilding(true);
    setError('');
    try {
      const created = await onBuild({
        creditCounselingUploadIds: creditCounselingIds,
        paymentAdviceUploadIds: paymentAdviceIds,
        serviceRecipients: selectedServiceRecipients,
        debnChoices,
      });
      setContext(current => ({
        ...current,
        packages: [created, ...(current?.packages || [])],
      }));
      setOpen(false);
    } catch (buildError) {
      setError(buildError.message || 'The filing package could not be created.');
    } finally {
      setBuilding(false);
    }
  };

  const downloadPackage = async packageRecord => {
    setError('');
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}${packageRecord.download_url}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(await responseMessage(response, 'Could not download the filing package.'));
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = packageRecord.filename || 'filing-package.zip';
      link.click();
      URL.revokeObjectURL(objectUrl);
    } catch (downloadError) {
      setError(downloadError.message);
    }
  };

  const viewDocument = async documentRecord => {
    setError('');
    setPreviewingDocumentId(documentRecord.id);
    try {
      const token = localStorage.getItem('accessToken');
      const viewUrl = documentRecord.view_url || `/uploaded-document/${documentRecord.id}/raw/`;
      const response = await fetch(`${API_URL}${viewUrl}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(await responseMessage(response, 'Could not open this document.'));
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      setPreview({
        url: objectUrl,
        filename: documentRecord.filename || 'Document preview',
      });
    } catch (viewError) {
      setError(viewError.message);
    } finally {
      setPreviewingDocumentId(null);
    }
  };

  const deletePackage = async packageRecord => {
    if (!window.confirm(`Delete ${packageRecord.filename}? This also removes the saved archive.`)) return;
    setError('');
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/case/${caseId}/filing-packages/${packageRecord.id}/`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(await responseMessage(response, 'Could not delete the filing package.'));
      setContext(current => ({
        ...current,
        packages: (current?.packages || []).filter(item => item.id !== packageRecord.id),
      }));
    } catch (deleteError) {
      setError(deleteError.message);
    }
  };

  return (
    <section className="mb-10 rounded-2xl border border-indigo-200 bg-indigo-50 p-5 shadow-sm">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Complete Filing Package</h2>
          <p className="mt-1 text-sm text-gray-600">
            Build and save one ZIP containing the separately fileable petition documents.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => {
              setOpen(true);
              loadContext();
            }}
            disabled={!ready || loading}
            className="rounded-lg bg-indigo-700 px-5 py-3 font-semibold text-white shadow hover:bg-indigo-800 disabled:cursor-not-allowed disabled:bg-gray-400"
          >
            Create Filing Package
          </button>
          <CMECFCaseFilingPanel caseId={caseId} onChanged={loadContext} />
        </div>
      </div>

      {error && <p className="mt-4 rounded-lg bg-red-100 px-4 py-3 text-sm text-red-800">{error}</p>}

      <div className="mt-6">
        <h3 className="font-semibold text-gray-800">Saved packages</h3>
        {loading ? (
          <p className="mt-2 text-sm text-gray-500">Loading saved packages…</p>
        ) : !(context?.packages || []).length ? (
          <p className="mt-2 text-sm text-gray-500">No filing packages have been saved yet.</p>
        ) : (
          <div className="mt-3 space-y-3">
            {context.packages.map(packageRecord => (
              <details key={packageRecord.id} className="rounded-lg border border-gray-200 bg-white p-3">
                <summary className="cursor-pointer font-medium text-gray-800">
                  {packageRecord.filename} · {formatCreatedAt(packageRecord.created_at)}
                </summary>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button type="button" onClick={() => downloadPackage(packageRecord)} className="rounded bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700">
                    Download ZIP
                  </button>
                  <button type="button" onClick={() => deletePackage(packageRecord)} disabled={packageRecord.can_delete === false} className="rounded bg-red-600 px-3 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-gray-400">
                    Delete
                  </button>
                </div>
                {packageRecord.can_delete === false && <p className="mt-2 text-xs text-gray-500">This package is preserved as part of its CM/ECF filing audit record.</p>}
                <ul className="mt-3 max-h-48 space-y-1 overflow-auto text-xs text-gray-600">
                  {(packageRecord.manifest || []).map(file => (
                    <li key={file.filename} className="flex items-center justify-between gap-3">
                      <span className="truncate">{file.filename}</span>
                      {file.view_url && (
                        <button type="button" onClick={() => viewDocument(file)} className="shrink-0 font-semibold text-indigo-700 hover:underline">View</button>
                      )}
                    </li>
                  ))}
                </ul>
              </details>
            ))}
          </div>
        )}
      </div>

      {open && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-2xl font-bold">Create Filing Package</h2>
                <p className="mt-1 text-sm text-gray-600">Review the checks and document selections before building.</p>
              </div>
              <button type="button" onClick={() => setOpen(false)} disabled={building} className="text-2xl text-gray-500 hover:text-gray-800">×</button>
            </div>

            <div className={`mt-5 rounded-lg p-4 ${context?.validation?.ready ? 'bg-green-50 text-green-900' : 'bg-red-50 text-red-900'}`}>
              <h3 className="font-semibold">Pre-filing checks</h3>
              {context?.validation?.ready ? (
                <p className="mt-1 text-sm">All property values and required Chapter 7 intentions are complete.</p>
              ) : (
                <>
                  {(context?.validation?.errors || []).map(message => <p key={message} className="mt-1 text-sm">{message}</p>)}
                  {!!context?.validation?.missing_asset_values?.length && (
                    <ul className="mt-2 list-disc pl-5 text-sm">
                      {context.validation.missing_asset_values.map(asset => <li key={`${asset.model}-${asset.id}`}>{asset.label}</li>)}
                    </ul>
                  )}
                  {!!context?.validation?.missing_statement_of_intents?.length && (
                    <ul className="mt-2 list-disc pl-5 text-sm">
                      {context.validation.missing_statement_of_intents.map(creditor => <li key={creditor.id}>{creditor.name}</li>)}
                    </ul>
                  )}
                </>
              )}
            </div>

            <div className="mt-5">
              <h3 className="font-semibold">Credit counseling certificates</h3>
              <p className="text-sm text-gray-600">Choose at least one certificate for each filing debtor. They will be merged in Debtor 1/Debtor 2 order.</p>
              {!counselingDocuments.length ? (
                <p className="mt-2 rounded bg-amber-50 p-3 text-sm text-amber-800">No credit-counseling uploads were found.</p>
              ) : counselingDocuments.map(document => (
                <div key={document.id} className="mt-2 flex items-start justify-between gap-3 rounded border p-3">
                  <label className="flex min-w-0 items-start gap-3">
                    <input
                      type="checkbox"
                      checked={creditCounselingIds.includes(document.id)}
                      disabled={!document.mergeable || document.status === 'rejected'}
                      onChange={() => toggleDocument(document.id, setCreditCounselingIds)}
                    />
                    <span className="min-w-0 text-sm"><span className="break-words font-medium">{document.filename}</span><br /><span className="text-gray-500">{document.checklist_item}</span></span>
                  </label>
                  <button
                    type="button"
                    onClick={() => viewDocument(document)}
                    disabled={previewingDocumentId === document.id}
                    className="shrink-0 rounded border border-blue-300 bg-blue-50 px-3 py-1 text-sm font-semibold text-blue-700 hover:bg-blue-100 disabled:cursor-wait disabled:opacity-60"
                  >
                    {previewingDocumentId === document.id ? 'Opening…' : 'View'}
                  </button>
                </div>
              ))}
              {!counselingComplete && <p className="mt-2 text-sm font-medium text-red-700">Select {filingDebtorCount} certificate{filingDebtorCount === 1 ? '' : 's'}.</p>}
            </div>

            <div className="mt-5">
              <h3 className="font-semibold">Debtor Electronic Noticing (DEBN)</h3>
              <p className="text-sm text-gray-600">Choose separately for each debtor. This choice is placed in that debtor&apos;s PDF and saved with the package.</p>
              <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {(context?.filing_debtors || []).map(debtor => (
                  <div key={debtor.id} className="rounded border p-3">
                    <div className="text-sm font-medium text-gray-900">{debtor.is_primary ? 'Debtor 1' : 'Debtor 2'}: {debtor.name}</div>
                    <label className="mt-2 flex items-start gap-2 text-sm">
                      <input type="radio" name={`package-debn-${debtor.id}`} checked={debnChoices[String(debtor.id)] === 'enroll'} onChange={() => setDebnChoices(current => ({ ...current, [String(debtor.id)]: 'enroll' }))} />
                      Enroll in Debtor Electronic Noticing
                    </label>
                    <label className="mt-2 flex items-start gap-2 text-sm">
                      <input type="radio" name={`package-debn-${debtor.id}`} checked={debnChoices[String(debtor.id)] === 'decline'} onChange={() => setDebnChoices(current => ({ ...current, [String(debtor.id)]: 'decline' }))} />
                      Decline Debtor Electronic Noticing
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-5">
              <h3 className="font-semibold">Payment advices attachments</h3>
              <p className="text-sm text-gray-600">Select the uploaded paystubs to merge. Leave these clear when the Payment Advices form states none are available.</p>
              {!paymentDocuments.length ? (
                <p className="mt-2 text-sm text-gray-500">No paystub uploads were found.</p>
              ) : paymentDocuments.map(document => (
                <div key={document.id} className="mt-2 flex items-start justify-between gap-3 rounded border p-3">
                  <label className="flex min-w-0 items-start gap-3">
                    <input
                      type="checkbox"
                      checked={paymentAdviceIds.includes(document.id)}
                      disabled={!document.mergeable || document.status === 'rejected'}
                      onChange={() => toggleDocument(document.id, setPaymentAdviceIds)}
                    />
                    <span className="min-w-0 text-sm"><span className="break-words font-medium">{document.filename}</span><br /><span className="text-gray-500">{document.checklist_item}</span></span>
                  </label>
                  <button
                    type="button"
                    onClick={() => viewDocument(document)}
                    disabled={previewingDocumentId === document.id}
                    className="shrink-0 rounded border border-blue-300 bg-blue-50 px-3 py-1 text-sm font-semibold text-blue-700 hover:bg-blue-100 disabled:cursor-wait disabled:opacity-60"
                  >
                    {previewingDocumentId === document.id ? 'Opening…' : 'View'}
                  </button>
                </div>
              ))}
            </div>

            {context?.chapter === '13' && (
              <div className="mt-5">
                <h3 className="font-semibold">Chapter 13 plan service</h3>
                <p className="text-sm text-gray-600">Select only creditors who must receive the plan by mail, then choose regular U.S. Mail or certified mail.</p>
                <div className="mt-2 max-h-64 space-y-2 overflow-auto">
                  {(context.service_creditors || []).map(creditor => (
                    <div key={creditor.id} className="flex flex-col gap-2 rounded border p-3 sm:flex-row sm:items-center sm:justify-between">
                      <label className="flex items-start gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={!!serviceMethods[creditor.id]}
                          onChange={event => setServiceMethods(current => ({
                            ...current,
                            [creditor.id]: event.target.checked ? 'USM' : undefined,
                          }))}
                        />
                        <span>
                          <span className="font-medium">{creditor.name}</span><br />
                          <span className={creditor.address_complete ? 'text-gray-500' : 'text-red-700'}>
                            {creditor.address_complete
                              ? `${creditor.street1}, ${creditor.city}, ${creditor.state} ${creditor.zip_code}`
                              : 'Complete this creditor’s mailing address before selecting it.'}
                          </span>
                        </span>
                      </label>
                      {!!serviceMethods[creditor.id] && (
                        <select
                          value={serviceMethods[creditor.id]}
                          onChange={event => setServiceMethods(current => ({ ...current, [creditor.id]: event.target.value }))}
                          className="rounded border px-2 py-1 text-sm"
                        >
                          <option value="USM">U.S. Mail</option>
                          <option value="Certified">Certified Mail</option>
                        </select>
                      )}
                    </div>
                  ))}
                </div>
                {incompleteServiceRecipient && <p className="mt-2 text-sm font-medium text-red-700">Complete the address for {incompleteServiceRecipient.name} or unselect it.</p>}
              </div>
            )}

            <div className="mt-6 flex justify-end gap-3">
              <button type="button" onClick={() => setOpen(false)} disabled={building} className="rounded border px-4 py-2 font-medium">Cancel</button>
              <button
                type="button"
                onClick={createPackage}
                disabled={building || !ready || !context?.validation?.ready || !counselingComplete || !debnChoicesComplete || !!incompleteServiceRecipient}
                className="rounded bg-indigo-700 px-5 py-2 font-semibold text-white hover:bg-indigo-800 disabled:cursor-not-allowed disabled:bg-gray-400"
              >
                {building ? 'Building filing package…' : 'Build and Save Package'}
              </button>
            </div>
          </div>
        </div>
      )}

      {preview && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 p-3 sm:p-6">
          <div className="flex h-[94vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between gap-4 border-b px-4 py-3">
              <h2 className="min-w-0 truncate text-lg font-bold text-gray-900">{preview.filename}</h2>
              <div className="flex shrink-0 items-center gap-2">
                <a
                  href={preview.url}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded border border-blue-300 bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100"
                >
                  Open in New Tab
                </a>
                <button
                  type="button"
                  onClick={() => setPreview(null)}
                  className="rounded bg-gray-800 px-3 py-2 text-sm font-semibold text-white hover:bg-gray-900"
                >
                  Close
                </button>
              </div>
            </div>
            <iframe
              src={preview.url}
              title={preview.filename}
              className="min-h-0 flex-1 bg-gray-100"
            />
          </div>
        </div>
      )}
    </section>
  );
}
